import { useTurnstile } from "@/shared/components/Turnstile/use-turnstile"
import { verifyTurnstileToken } from "@/shared/http/turnstile"
import { sendMyQuote, SendMyQuoteBody, SendMyQuoteError } from "@/shared/http/lead-api/send-my-quote"
import { useMutation } from "@tanstack/react-query"
import { TrackJS } from "trackjs-nextjs"

export type SendMyQuoteErrorKind = "cooldown" | "generic"

export function classifyError(error: unknown): SendMyQuoteErrorKind {
  if (error instanceof SendMyQuoteError) {
    const { status, code } = error
    if (
      (status === 409 && code === "already_sent") ||
      (status === 429 && code === "rate_limited")
    ) {
      return "cooldown"
    }
  }
  return "generic"
}

export const useSendMyQuote = () => {
  const { getToken, resetToken } = useTurnstile()

  const mutation = useMutation({
    mutationKey: ["send-my-quote"],
    mutationFn: async (data: SendMyQuoteBody) => {
      await verifyTurnstileToken(getToken(), resetToken)
      try {
        return await sendMyQuote(data)
      } catch (error) {
        if (error instanceof SendMyQuoteError && error.status === 400) {
          resetToken()
          await verifyTurnstileToken(getToken(), resetToken)
          return await sendMyQuote(data)
        }
        throw error
      }
    },
    onError: (error: unknown) => {
      const kind = classifyError(error)
      const isFastPathKillSwitch = error instanceof SendMyQuoteError && error.status === 503
      if (kind === "generic" && !isFastPathKillSwitch) {
        TrackJS.track(error as string)
      }
    },
  })

  return {
    ...mutation,
    errorKind: mutation.error != null ? classifyError(mutation.error) : null,
  }
}
